import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import SectionTitle from '../yourProperties/Title';
import variables from '../../../stylesheets/variables.module.scss';
import { BookingRoomState } from '../../../types/booking/BookingTypes';
import useTranslate from '../../../customHooks/useTranslate';
import useLanguage from '../../../customHooks/useLanguage';
import Card from '../../../muiComponents/Card';
import Grid from '../../../muiComponents/Grid';
import GridChild from '../../../muiComponents/GridChild';
import {
    AGE_SEGMENT_COLOR,
    avatarColumnGrid,
    pricesColumnGrid,
    taxNSupplementsColumnGrid,
} from '../../../utils/bookings/bookingUtils';
import Title from '../../components/Title';
import { formatNumber, getOrdinalNumber } from '../../../utils/usefulFunctions';
import { addDaysToDate, formatShortDate } from '../../../utils/formatDates';
import SupplierTaxCalculationTypeIcon from './taxTypesIcons/SupplierTaxCalculationTypeIcon';
import SupplierTaxCalculationRuleTypeIcon from './taxTypesIcons/SupplierTaxCalculationRuleTypeIcon';
import BookingRoomAvatar from './bookingDetail/avatars/BookingRoomAvatar';
import Tag from '../../../muiComponents/Tag';
import {
    full_ColumnGrid,
    gridSpacing20,
    gridSpacing30,
    singleMD_ColumnGrid,
} from '../../../utils/commonVariables/grid';

interface RoomInfoProps {
    room: BookingRoomState;
    checkIn: Date | null;
    currencyKey: string;
}

const RoomInfo = (props: RoomInfoProps) => {
    const { room, checkIn, currencyKey } = props;
    const translate = useTranslate();
    const language = useLanguage();

    return (
        <Card
            className='booking__card flex-column gap-20'
            style={{ height: '100%', padding: '30px' }}
        >
            <Grid
                style={{ width: 'auto' }}
                columnSpacing={gridSpacing30}
                rowSpacing={gridSpacing20}
            >
                <GridChild gridProps={full_ColumnGrid}>
                    <Grid
                        style={{ width: 'auto' }}
                        columnSpacing={gridSpacing30}
                        rowSpacing={gridSpacing20}
                    >
                        <GridChild gridProps={full_ColumnGrid}>
                            <SectionTitle text={room.roomType} />
                        </GridChild>
                        <GridChild gridProps={singleMD_ColumnGrid}>
                            <div className='flex-column gap-5'>
                                <Title
                                    className='text-capitalize'
                                    style={{
                                        color: 'var(--secondary-600)',
                                    }}
                                >
                                    {translate('room_type')}
                                </Title>

                                <p>{room.roomType || '----'}</p>
                            </div>
                        </GridChild>
                        <GridChild gridProps={singleMD_ColumnGrid}>
                            <div className='flex-column gap-5'>
                                <Title
                                    className='text-capitalize'
                                    style={{
                                        color: 'var(--secondary-600)',
                                    }}
                                >
                                    {translate('meal_plan')}
                                </Title>

                                <p>{room.mealPlan || '----'}</p>
                            </div>
                        </GridChild>
                    </Grid>
                </GridChild>
            </Grid>
            <div className='padding-top-20 border-color-top'>
                <Title className='text-capitalize  font-size-18'>
                    {translate('guests')}
                </Title>
                <Grid
                    style={{
                        width: 'auto',
                        padding: 20,
                    }}
                    columnSpacing={gridSpacing30}
                    rowSpacing={gridSpacing20}
                >
                    {room.guests.map((guest, gueInd) => {
                        return (
                            <GridChild
                                key={guest.name + gueInd}
                                gridProps={avatarColumnGrid}
                            >
                                <div
                                    key={guest.name}
                                    className='flex flex-align-center gap-10'
                                >
                                    <BookingRoomAvatar
                                        ageSegment={guest.ageSegment}
                                    />
                                    <div className='flex-column gap-5'>
                                        <Title className='text-capitalize'>
                                            {guest.name}
                                        </Title>
                                        <div className='flex gap-10 flex-align-center'>
                                            <p>
                                                {guest.age} {translate('years')}
                                            </p>
                                            <Tag
                                                uppercase
                                                text={translate(
                                                    guest?.ageSegment?.toLowerCase()
                                                )}
                                                color={
                                                    AGE_SEGMENT_COLOR[
                                                        guest.ageSegment
                                                    ]
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </GridChild>
                        );
                    })}
                </Grid>
            </div>

            <Grid
                style={{
                    width: 'auto',
                    borderTop: '1px solid var(--border)',
                    paddingTop: 20,
                }}
                columnSpacing={gridSpacing30}
                rowSpacing={gridSpacing20}
            >
                <GridChild gridProps={pricesColumnGrid}>
                    <div className='flex-column gap-10 '>
                        <Title className='text-seconday-700 '>
                            {translate('price_per_night')}
                        </Title>
                        <Table className='border-color  overflow-hidden'>
                            <TableHead>
                                <TableRow>
                                    <TableCell className='text-capitalize'>
                                        {translate('night')}
                                    </TableCell>
                                    <TableCell className='text-capitalize'>
                                        {translate('price')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {room.prices.map((price, priceInd) => {
                                    const dateOfRate = addDaysToDate(
                                        checkIn,
                                        priceInd
                                    );
                                    return (
                                        <TableRow key={price.id}>
                                            <TableCell>
                                                <div>
                                                    <Title className='booking__small-label'>
                                                        {`${getOrdinalNumber(
                                                            priceInd + 1,
                                                            language
                                                        )} ${translate(
                                                            'night'
                                                        )}`}
                                                    </Title>
                                                    <p>
                                                        {formatShortDate(
                                                            dateOfRate,
                                                            language
                                                        )}{' '}
                                                        -{' '}
                                                        {formatShortDate(
                                                            addDaysToDate(
                                                                dateOfRate,
                                                                1
                                                            ),
                                                            language
                                                        )}
                                                    </p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <p
                                                    className={`font-size-18 width-content `}
                                                >
                                                    {currencyKey && price.amount
                                                        ? formatNumber(
                                                              price.amount,
                                                              'currency',
                                                              currencyKey
                                                          )
                                                        : price.amount
                                                        ? formatNumber(
                                                              price.amount,
                                                              'decimal'
                                                          )
                                                        : '----'}
                                                </p>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </GridChild>
                <GridChild gridProps={taxNSupplementsColumnGrid}>
                    <div className='flex-column gap-40'>
                        <div className='flex-column gap-10'>
                            <Title className='text-seconday-700 '>
                                {translate('taxes')}
                            </Title>
                            <Table className='border-color  overflow-hidden'>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            {translate('description2')}
                                        </TableCell>
                                        <TableCell>
                                            {translate('type')}
                                        </TableCell>
                                        <TableCell>
                                            {translate('amount')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {room.taxes.length ? (
                                        room.taxes.map(tax => {
                                            return (
                                                <TableRow key={tax.id}>
                                                    <TableCell>
                                                        <div className='flex-column gap-5 '>
                                                            <Title className=' text-seconday-700 '>
                                                                {tax.name}
                                                            </Title>
                                                            <p>
                                                                {
                                                                    tax.description
                                                                }
                                                            </p>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className='flex gap-5 flex-wrap'>
                                                            <SupplierTaxCalculationTypeIcon
                                                                color={
                                                                    variables.accentColorCyan400
                                                                }
                                                                entityKey={
                                                                    tax.supplierTaxCalculationTypeKey
                                                                }
                                                                text={
                                                                    tax.supplierTaxCalculationType
                                                                }
                                                            />
                                                            <SupplierTaxCalculationRuleTypeIcon
                                                                color={
                                                                    variables.primary200
                                                                }
                                                                entityKey={
                                                                    tax.supplierTaxCalculationRuleTypeKey
                                                                }
                                                                type='subject'
                                                            />
                                                            <SupplierTaxCalculationRuleTypeIcon
                                                                color={
                                                                    variables.accentColorPurple400
                                                                }
                                                                entityKey={
                                                                    tax.supplierTaxCalculationRuleTypeKey
                                                                }
                                                                type='frequency'
                                                            />
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatNumber(
                                                            tax.amount,
                                                            tax.currencyKey
                                                                ? 'currency'
                                                                : 'decimal',
                                                            tax.currencyKey
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={4}>
                                                {translate('no_results')}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                        <div
                            id={`room-${room.id}-supplements`}
                            className='flex-column gap-10'
                        >
                            <Title className='text-seconday-700 '>
                                {translate('supplements')}
                            </Title>
                            <Table className='border-color  overflow-hidden'>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            {translate('description2')}
                                        </TableCell>
                                        <TableCell>
                                            {translate('type')}
                                        </TableCell>
                                        <TableCell>
                                            {translate('amount')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {room.supplements.length ? (
                                        room.supplements.map(supplement => {
                                            console.log(
                                                supplement.supplierTaxCalculationTypeKey
                                            );
                                            return (
                                                <TableRow key={supplement.id}>
                                                    <TableCell>
                                                        <div className='flex-column gap-5 '>
                                                            <Title className=' text-seconday-700 '>
                                                                {
                                                                    supplement.name
                                                                }
                                                            </Title>
                                                            <p>
                                                                {
                                                                    supplement.description
                                                                }
                                                            </p>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className='flex gap-5 flex-wrap'>
                                                            <div>
                                                                <SupplierTaxCalculationTypeIcon
                                                                    color={
                                                                        variables.accentColorCyan400
                                                                    }
                                                                    entityKey={
                                                                        supplement.supplierTaxCalculationTypeKey
                                                                    }
                                                                    text={
                                                                        supplement.supplierTaxCalculationType
                                                                    }
                                                                />
                                                            </div>
                                                            <SupplierTaxCalculationRuleTypeIcon
                                                                color={
                                                                    variables.primary200
                                                                }
                                                                entityKey={
                                                                    supplement.supplierTaxCalculationRuleTypeKey
                                                                }
                                                                type='subject'
                                                            />
                                                            <SupplierTaxCalculationRuleTypeIcon
                                                                color={
                                                                    variables.accentColorPurple400
                                                                }
                                                                entityKey={
                                                                    supplement.supplierTaxCalculationRuleTypeKey
                                                                }
                                                                type='frequency'
                                                            />
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatNumber(
                                                            supplement.amount,
                                                            supplement.currencyKey
                                                                ? 'currency'
                                                                : 'decimal',
                                                            supplement.currencyKey
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell
                                                colSpan={4}
                                                sx={{ textAlign: 'center' }}
                                            >
                                                {translate('no_results')}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                </GridChild>
            </Grid>
        </Card>
    );
};

export default RoomInfo;
